import { NavLink } from 'react-router-dom';
import {
  LayoutDashboard,
  CalendarDays,
  X,
  Phone,
  MessageCircle,
  MapPin,
  Navigation,
  CreditCard,
  ClipboardList,
  Contact
} from 'lucide-react';
import { stripHTML } from 'utils/helpers';
import { PORTAL_ROUTES, EXTERNAL_URLS } from 'utils/constants';
import { getPractice } from 'library/auth';

interface SidebarProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

interface NavItem {
  label: string;
  icon: React.ReactNode;
  path: string;
}

export const Sidebar = ({ mobileOpen, onMobileClose }: SidebarProps) => {
  const practiceInfo = getPractice()

  const mainNavItems: NavItem[] = [
    { label: 'Dashboard', icon: <LayoutDashboard size={20} />, path: PORTAL_ROUTES.DASHBOARD },
    { label: 'My Appointments', icon: <CalendarDays size={20} />, path: PORTAL_ROUTES.APPOINTMENTS },
    { label: 'Conversations', icon: <MessageCircle size={20} />, path: PORTAL_ROUTES.CONVERSATIONS },
    { label: 'Treatment Plan', icon: <ClipboardList size={20} />, path: PORTAL_ROUTES.TREATMENT_PLANS },
    { label: 'Billing', icon: <CreditCard size={20} />, path: PORTAL_ROUTES.BILLING },
    ...(practiceInfo?.patient_forms_access
      ? [{ label: 'Profile & Forms', icon: <Contact size={20} />, path: PORTAL_ROUTES.PROFILE }]
      : []),
  ];

  const sidebarContent = (
    <div className="flex flex-col h-full px-4 py-2">
      {/* Main nav */}
      <nav className="space-y-1">
        {mainNavItems.map((item) => (
          <NavLink
            key={item.label}
            to={item.path}
            onClick={() => onMobileClose?.()}
            className={({ isActive }) =>
              `w-full flex items-center gap-3 px-4 py-3 rounded-xl text-[14px] font-medium transition-all duration-200 ${isActive
                ? 'bg-secondary-900 text-white shadow-md'
                : 'text-secondary-500 hover:bg-gray-100 hover:text-secondary-800'
              }`
            }
          >
            {item.icon}
            {item.label}
          </NavLink>
        ))}
      </nav>

      {/* Practice info card */}
      {practiceInfo && (
        <div className="mt-auto mb-4">
          <div className="bg-secondary-50 rounded-2xl p-4 space-y-3">
            <p className="text-sm font-bold text-secondary-800">Need help?</p>
            {practiceInfo.phone_number && (
              <a
                href={`tel:${practiceInfo.phone_number}`}
                className="flex items-center gap-2 text-xs text-secondary-500 hover:text-secondary-700 transition-colors"
              >
                <Phone size={14} />
                {practiceInfo.phone_number}
              </a>
            )}
            {practiceInfo.address && (
              <div className="flex items-start gap-2 text-xs text-secondary-500">
                <MapPin size={14} className="mt-0.5 shrink-0" />
                <span>{stripHTML(practiceInfo.address)}</span>
              </div>
            )}
            {practiceInfo.address && (
              <a
                href={`${EXTERNAL_URLS.GOOGLE_MAPS_SEARCH}?api=1&query=${encodeURIComponent(`${practiceInfo.title} ${stripHTML(practiceInfo.address)}`)}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center justify-center gap-2 w-full bg-secondary-900 text-white text-xs font-semibold py-2 rounded-xl hover:bg-secondary-800 transition-colors"
              >
                <Navigation size={14} />
                Get Directions
              </a>
            )}
          </div>
        </div>
      )}

      {/* Powered by badge */}
      <div className="flex justify-center">
        <div className="flex items-center gap-2 whitespace-nowrap bg-white/90 backdrop-blur-sm border border-gray-200 rounded-full px-5 py-1.5 shadow-md">
          <span className="text-[11px] text-secondary-400">Powered by</span>
          <img src="/logo.png" alt="TeamCare" className="h-4 w-4 object-contain" />
          <span className="text-[11px] font-semibold text-secondary-600">TeamCare</span>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Desktop sidebar — owns its own card chrome so Layout can render this once. */}
      <aside className="hidden md:flex md:flex-col w-[240px] min-w-[240px] bg-white rounded-2xl shadow-sm border border-gray-100 overflow-y-auto h-full">
        {sidebarContent}
      </aside>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-40 md:hidden">
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm" onClick={onMobileClose} />
          <aside className="fixed left-0 top-0 bottom-0 w-[280px] bg-white shadow-2xl z-50 flex flex-col">
            <div className="flex items-center justify-between h-16 px-5 border-b border-gray-100">
              <span className="text-lg font-bold text-secondary-900">Menu</span>
              <button
                onClick={onMobileClose}
                className="w-8 h-8 flex items-center justify-center text-secondary-400 hover:text-secondary-600 hover:bg-gray-100 rounded-lg"
              >
                <X size={18} />
              </button>
            </div>
            {sidebarContent}
          </aside>
        </div>
      )}
    </>
  );
};
